import { h, Component } from 'preact';
import util from 'preact-util';
import { Text } from 'preact-i18n';

class TranscribeVideoButton extends Component {
    constructor(props) {
        super(props);
        this.state = {
            transcribing: false,
            error: null,
            success: false,
            showLanguageOptions: false,
            selectedLanguages: {
                en: true,
                es: false,
                fr: false,
                fi: false,
            },
        };
    }

    formatTimestamp = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);

        if (hours > 0) {
            return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
        }
        return `${minutes}:${String(secs).padStart(2, '0')}`;
    }

    toggleLanguage = (lang) => {
        const { selectedLanguages } = this.state;
        this.setState({
            selectedLanguages: {
                ...selectedLanguages,
                [lang]: !selectedLanguages[lang]
            }
        });
    }

    handleTranscribe = async (translateOnly = false) => {
        const { articleId, fileIndex, language = 'no', onComplete } = this.props;
        const { selectedLanguages } = this.state;

        if (!articleId || fileIndex === undefined) {
            console.error('Article ID and file index are required');
            return;
        }

        // Build array of selected translation languages
        const translationLanguages = [];
        if (selectedLanguages.en) translationLanguages.push('en');
        if (selectedLanguages.es) translationLanguages.push('es');
        if (selectedLanguages.fr) translationLanguages.push('fr');
        if (selectedLanguages.fi) translationLanguages.push('fi');

        // If translate-only mode, ensure we have at least one language selected
        if (translateOnly && translationLanguages.length === 0) {
            this.setState({ error: 'Please select at least one language to translate to' });
            return;
        }

        this.setState({ transcribing: true, error: null, success: false });

        try {
            const response = await util.fetchApi(`/api/articles/${articleId}/transcribe-video`, {
                method: 'POST',
            }, {
                fileIndex,
                language,
                translationLanguages: translateOnly ? translationLanguages : undefined,
            });

            if (response.status === 200) {
                this.setState({ transcribing: false, success: true });
                console.log('Transcription successful:', response.data);

                // Call onComplete callback if provided
                if (onComplete) {
                    onComplete(response.data);
                }

                // Reset success message after 3 seconds
                setTimeout(() => {
                    this.setState({ success: false });
                }, 3000);
            } else {
                this.setState({
                    transcribing: false,
                    error: response.message || 'Transcription failed',
                });
            }
        } catch (error) {
            console.error('Transcription error:', error);
            this.setState({
                transcribing: false,
                error: error.message || 'Transcription failed',
            });
        }
    }

    render() {
        const { file, small = false } = this.props;
        const { transcribing, error, success, showLanguageOptions, selectedLanguages } = this.state;

        // Check if file already has a transcript
        const hasTranscript = file && file.transcript;

        return (
            <div class='d-flex flex-column gap-2'>
                <div class='d-flex gap-2 align-items-center'>
                    <button
                        type='button'
                        class={`btn ${hasTranscript ? 'btn-outline-primary' : 'btn-primary'} ${small ? 'btn-sm' : ''} rounded-pill`}
                        onClick={this.handleTranscribe}
                        disabled={transcribing}
                        title={hasTranscript ? 'Re-transcribe video' : 'Transcribe video'}
                    >
                        {transcribing ? (
                            <>
                                <i class='fa-solid fa-spinner fa-spin me-2' />
                                <Text id='admin.transcribing'>Transcribing...</Text>
                            </>
                        ) : hasTranscript ? (
                            <>
                                <i class='fa-solid fa-rotate me-2' />
                                <Text id='admin.retranscribe'>Re-transcribe</Text>
                            </>
                        ) : (
                            <>
                                <i class='fa-solid fa-closed-captioning me-2' />
                                <Text id='admin.transcribe'>Transcribe</Text>
                            </>
                        )}
                    </button>

                    <button
                        type='button'
                        class='btn btn-sm btn-outline-secondary'
                        onClick={() => this.setState({ showLanguageOptions: !showLanguageOptions })}
                        title='Select translation languages'
                    >
                        <i class='fa-solid fa-language' />
                    </button>

                    {hasTranscript && (
                        <button
                            type='button'
                            class='btn btn-sm btn-outline-success rounded-pill'
                            onClick={() => this.handleTranscribe(true)}
                            disabled={transcribing}
                            title='Add translations only (skip transcription)'
                        >
                            <i class='fa-solid fa-language me-1' />
                            <Text id='admin.translate-only'>Translate</Text>
                        </button>
                    )}
                </div>

                {showLanguageOptions && (
                    <div class='card'>
                        <div class='card-body py-2'>
                            <small class='text-muted d-block mb-2'><Text id='admin.select-translation-languages'>Select translation languages:</Text></small>
                            <div class='d-flex flex-column gap-1'>
                                <div class='form-check'>
                                    <input
                                        class='form-check-input'
                                        type='checkbox'
                                        id='lang-en'
                                        checked={selectedLanguages.en}
                                        onChange={() => this.toggleLanguage('en')}
                                    />
                                    <label class='form-check-label' for='lang-en'>
                                        <small>English (EN)</small>
                                    </label>
                                </div>
                                <div class='form-check'>
                                    <input
                                        class='form-check-input'
                                        type='checkbox'
                                        id='lang-es'
                                        checked={selectedLanguages.es}
                                        onChange={() => this.toggleLanguage('es')}
                                    />
                                    <label class='form-check-label' for='lang-es'>
                                        <small>Spanish (ES)</small>
                                    </label>
                                </div>
                                <div class='form-check'>
                                    <input
                                        class='form-check-input'
                                        type='checkbox'
                                        id='lang-fr'
                                        checked={selectedLanguages.fr}
                                        onChange={() => this.toggleLanguage('fr')}
                                    />
                                    <label class='form-check-label' for='lang-fr'>
                                        <small>French (FR)</small>
                                    </label>
                                </div>
                                <div class='form-check'>
                                    <input
                                        class='form-check-input'
                                        type='checkbox'
                                        id='lang-fi'
                                        checked={selectedLanguages.fi}
                                        onChange={() => this.toggleLanguage('fi')}
                                    />
                                    <label class='form-check-label' for='lang-fi'>
                                        <small>Finnish (FI)</small>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {success && (
                    <div class='alert alert-success mb-0 py-2' role='alert'>
                        <i class='fa-solid fa-check-circle me-2' />
                        <small><Text id='admin.transcription-success'>Video transcribed successfully!</Text></small>
                    </div>
                )}

                {error && (
                    <div class='alert alert-danger mb-0 py-2' role='alert'>
                        <i class='fa-solid fa-exclamation-triangle me-2' />
                        <small>{error}</small>
                    </div>
                )}

                {hasTranscript && (
                    <div class='card mb-0'>
                        <div class='card-body py-2'>
                            <p class='card-text mb-1'>
                                <small class='text-muted'>
                                    <i class='fa-solid fa-closed-captioning me-1' />
                                    <Text id='admin.transcript-available'>Transcript available</Text>
                                    {file.transcriptionLanguage && ` (${file.transcriptionLanguage})`}
                                    {file.transcriptEnglish && <span class='badge bg-info ms-2'>EN</span>}
                                    {file.transcriptSpanish && <span class='badge bg-info ms-2'>ES</span>}
                                    {file.transcriptFrench && <span class='badge bg-info ms-2'>FR</span>}
                                    {file.transcriptFinnish && <span class='badge bg-info ms-2'>FI</span>}
                                </small>
                            </p>
                            {file.transcriptionDate && (
                                <p class='card-text mb-0'>
                                    <small class='text-muted'>
                                        <i class='fa-solid fa-clock me-1' />
                                        {new Date(file.transcriptionDate).toLocaleString()}
                                    </small>
                                </p>
                            )}
                            {file.transcript && (
                                <details class='mt-2'>
                                    <summary class='cursor-pointer'>
                                        <small><Text id='admin.view-transcript'>View transcript</Text></small>
                                    </summary>
                                    <div class='mt-2 p-2 bg-light rounded' style='max-height: 400px; overflow-y: auto;'>
                                        {file.transcriptionSegments && file.transcriptionSegments.length > 0 ? (
                                            <div class='d-flex flex-column gap-2'>
                                                {file.transcriptionSegments.map((segment, idx) => {
                                                    const translations = [
                                                        { lang: 'EN', segment: file.transcriptionSegmentsEnglish && file.transcriptionSegmentsEnglish[idx], color: 'primary' },
                                                        { lang: 'ES', segment: file.transcriptionSegmentsSpanish && file.transcriptionSegmentsSpanish[idx], color: 'success' },
                                                        { lang: 'FR', segment: file.transcriptionSegmentsFrench && file.transcriptionSegmentsFrench[idx], color: 'info' },
                                                        { lang: 'FI', segment: file.transcriptionSegmentsFinnish && file.transcriptionSegmentsFinnish[idx], color: 'warning' },
                                                    ];

                                                    return (
                                                        <div key={`segment-${idx}`} class='d-flex gap-2 border-bottom pb-2'>
                                                            <div class='text-muted' style='min-width: 80px; font-family: monospace;'>
                                                                <small>
                                                                    <strong>{this.formatTimestamp(segment.start)}</strong>
                                                                    {segment.end && ` - ${this.formatTimestamp(segment.end)}`}
                                                                </small>
                                                            </div>
                                                            <div class='flex-grow-1'>
                                                                <div class='mb-1'>
                                                                    <small><strong>{segment.text}</strong></small>
                                                                </div>
                                                                {translations.map(({ lang, segment: translatedSegment, color }) =>
                                                                    translatedSegment && translatedSegment.text ? (
                                                                        <div key={lang} class={`text-${color} mt-1`} style='opacity: 0.8;'>
                                                                            <small>
                                                                                <span class={`badge bg-${color} me-1`} style='font-size: 0.7em;'>{lang}</span>
                                                                                {translatedSegment.text}
                                                                            </small>
                                                                        </div>
                                                                    ) : null
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div>
                                                <div class='mb-3'>
                                                    <small style='white-space: pre-wrap;'><strong>{file.transcript}</strong></small>
                                                </div>

                                                {file.transcriptEnglish && (
                                                    <div class='border-top pt-3 text-primary' style='opacity: 0.8;'>
                                                        <div class='mb-2'>
                                                            <small class='fw-bold'>
                                                                <span class='badge bg-primary me-1'>EN</span>
                                                                English Translation:
                                                            </small>
                                                        </div>
                                                        <small style='white-space: pre-wrap;'>{file.transcriptEnglish}</small>
                                                    </div>
                                                )}

                                                {file.transcriptSpanish && (
                                                    <div class='border-top pt-3 text-success' style='opacity: 0.8;'>
                                                        <div class='mb-2'>
                                                            <small class='fw-bold'>
                                                                <span class='badge bg-success me-1'>ES</span>
                                                                Spanish Translation:
                                                            </small>
                                                        </div>
                                                        <small style='white-space: pre-wrap;'>{file.transcriptSpanish}</small>
                                                    </div>
                                                )}

                                                {file.transcriptFrench && (
                                                    <div class='border-top pt-3 text-info' style='opacity: 0.8;'>
                                                        <div class='mb-2'>
                                                            <small class='fw-bold'>
                                                                <span class='badge bg-info me-1'>FR</span>
                                                                French Translation:
                                                            </small>
                                                        </div>
                                                        <small style='white-space: pre-wrap;'>{file.transcriptFrench}</small>
                                                    </div>
                                                )}

                                                {file.transcriptFinnish && (
                                                    <div class='border-top pt-3 text-warning' style='opacity: 0.8;'>
                                                        <div class='mb-2'>
                                                            <small class='fw-bold'>
                                                                <span class='badge bg-warning me-1'>FI</span>
                                                                Finnish Translation:
                                                            </small>
                                                        </div>
                                                        <small style='white-space: pre-wrap;'>{file.transcriptFinnish}</small>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </details>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default TranscribeVideoButton;
